/**
 * Google Drive Knowledge Connector — indexes user documents for report context.
 *
 * Flow:
 * 1. User authorizes Google Drive access via OAuth (in browser)
 * 2. System lists relevant documents (PDFs, Docs, Sheets)
 * 3. Extracts text content and stores summaries in Firestore
 * 4. Summaries are used as context for Gemini report generation
 *
 * Document index stored at: users/{uid}/driveIndex/{docId}
 */

export interface DriveDocument {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  summary: string;      // AI-extracted summary of document content
  tags: string[];        // Auto-extracted topics: 'nuclear', 'cooling', 'grid', etc.
  indexedAt: string;
}

export interface DriveIndexState {
  isConnected: boolean;
  documents: DriveDocument[];
  lastSyncedAt: string | null;
  isIndexing: boolean;
}

import { looksLikeDocumentImageText } from './extractedTextQuality';

const DRIVE_SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

/**
 * Initialize Google Drive OAuth flow.
 * Must be called from the browser — opens a consent popup.
 */
export async function connectGoogleDrive(googleMapsApiKey: string): Promise<string | null> {
  try {
    // Use the Google Identity Services library for OAuth
    // The Maps API key project should have Drive API enabled
    const clientId = await getOAuthClientId();
    if (!clientId) {
      console.error('No OAuth Client ID configured for Google Drive');
      return null;
    }

    return new Promise((resolve) => {
      const tokenClient = (window as any).google?.accounts?.oauth2?.initTokenClient({
        client_id: clientId,
        scope: DRIVE_SCOPES,
        callback: (response: any) => {
          if (response.access_token) {
            resolve(response.access_token);
          } else {
            resolve(null);
          }
        },
      });
      tokenClient?.requestAccessToken();
    });
  } catch (e) {
    console.error('Drive OAuth failed:', e);
    return null;
  }
}

/**
 * List documents from user's Google Drive.
 */
export async function listDriveDocuments(accessToken: string): Promise<{ id: string; name: string; mimeType: string; modifiedTime: string }[]> {
  const query = encodeURIComponent("mimeType='application/pdf' or mimeType='application/vnd.google-apps.document' or mimeType='application/vnd.google-apps.spreadsheet'");
  const url = `https://www.googleapis.com/drive/v3/files?q=${query}&fields=files(id,name,mimeType,modifiedTime)&orderBy=modifiedTime desc&pageSize=50`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) throw new Error(`Drive API: ${res.status}`);
  const json = await res.json();
  return json.files || [];
}

/**
 * Extract text content from a Google Drive document.
 */
export async function extractDocumentText(accessToken: string, fileId: string, mimeType: string): Promise<string> {
  let url: string;

  if (mimeType === 'application/vnd.google-apps.document') {
    // Google Docs — export as plain text
    url = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=text/plain`;
  } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
    // Google Sheets — export as CSV
    url = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=text/csv`;
  } else {
    // PDFs and other files — download content (text extraction limited)
    url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
  }

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) return '';
  const text = await res.text();
  // Truncate to 10K chars for summarization
  return text.slice(0, 10000);
}

/**
 * Summarize a document using Gemini and extract relevant tags.
 */
/** Derive a reasonable summary from the filename when Gemini is unavailable. */
export function deriveSummaryFromFilename(fileName: string): string {
  // Strip extension and clean up
  const base = fileName.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
  return base || `Document: ${fileName}`;
}

/** Derive topic tags from filename patterns. */
export function deriveTagsFromFilename(fileName: string): string[] {
  const name = fileName.toLowerCase();
  const tags: string[] = [];
  if (name.includes('electric') || name.includes('grid') || name.includes('load') || name.includes('power')) tags.push('power', 'grid');
  if (name.includes('gas') || name.includes('pipeline')) tags.push('gas');
  if (name.includes('water') || name.includes('sewer')) tags.push('water');
  if (name.includes('environment') || name.includes('epa')) tags.push('environmental');
  if (name.includes('permit') || name.includes('zoning') || name.includes('ordinance')) tags.push('regulatory');
  if (name.includes('title') || name.includes('deed') || name.includes('easement') || name.includes('option')) tags.push('site', 'regulatory');
  if (name.includes('fiber') || name.includes('telecom')) tags.push('grid');
  if (name.includes('seismic') || name.includes('geotechnical') || name.includes('formation') || name.includes('soil')) tags.push('seismic');
  if (name.includes('nuclear') || name.includes('smr') || name.includes('reactor')) tags.push('nuclear');
  if (name.includes('solar') || name.includes('wind') || name.includes('renewable')) tags.push('renewable');
  if (name.includes('cost') || name.includes('financial') || name.includes('budget') || name.includes('scoring')) tags.push('cost');
  if (name.includes('sale') || name.includes('deck') || name.includes('overview') || name.includes('matrix')) tags.push('site');
  if (name.includes('map') || name.includes('survey') || name.includes('boundary')) tags.push('site', 'survey');
  return tags.length > 0 ? [...new Set(tags)] : ['site'];
}

export async function summarizeDocument(
  text: string,
  fileName: string,
  archetype?: import('../../types/company').CompanyArchetype,
  opts?: { signal?: AbortSignal; throwOnAbortOrTimeout?: boolean; timeoutMs?: number },
): Promise<{ summary: string; tags: string[]; deferred?: boolean }> {
  const name = fileName.toLowerCase();
  // For images/geographic files — derive tags from filename, skip Gemini.
  //
  // DIM-1 exception: when the image's vision extraction reads like an
  // image OF A DOCUMENT (screenshot / scan / photo of a spreadsheet,
  // table, letter…), fall through to the normal document summariser
  // with the vision text. The extension short-circuit below is what
  // stamped a Kinder Morgan cost-estimate screenshot with summary
  // "Site photograph/image: …" + tags ['site'] and doomed every
  // downstream consumer (relevance scores, sedSectionsHelpful) that
  // reads the summary instead of the text.
  if (name.match(/\.(jpg|jpeg|png|gif|bmp|tiff|svg)$/) && !looksLikeDocumentImageText(text)) {
    const tags: string[] = [];
    if (name.includes('site') || name.includes('aerial')) tags.push('site');
    if (name.includes('zone') || name.includes('zoning')) tags.push('regulatory', 'zoning');
    if (name.includes('map')) tags.push('survey');
    if (name.includes('pipeline')) tags.push('gas');
    return { summary: `Site photograph/image: ${fileName}`, tags: tags.length > 0 ? tags : ['site'] };
  }
  if (name.match(/\.(kmz|kml)$/)) {
    return { summary: `Geographic boundary/overlay data: ${fileName}`, tags: ['site', 'survey'] };
  }
  if (name.match(/\.(mp4|mov|avi|webm)$/)) {
    return { summary: `Site video: ${fileName}`, tags: ['site'] };
  }
  if (!text || text.length < 50) return { summary: `Document: ${fileName}`, tags: [] };

  // TU1/TU2 — backstop abort timeout on the summarise proxy fetch.
  const SUMMARIZE_FETCH_TIMEOUT_MS = opts?.timeoutMs ?? 90_000;
  const { authedFetch } = await import('../../lib/authedFetch');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error('summarize timeout')), SUMMARIZE_FETCH_TIMEOUT_MS);
  const callerSignal = opts?.signal;
  const onCallerAbort = () => controller.abort(callerSignal?.reason ?? new Error('aborted'));
  if (callerSignal) {
    if (callerSignal.aborted) {
      clearTimeout(timer);
      if (opts?.throwOnAbortOrTimeout) {
        throw new Error('summarize aborted');
      }
      return { summary: deriveSummaryFromFilename(fileName), tags: deriveTagsFromFilename(fileName), deferred: true };
    }
    callerSignal.addEventListener('abort', onCallerAbort, { once: true });
  }
  try {
    // Route through the same Gemini proxy used by the main pipeline.
    const res = await authedFetch('/api/gemini/summarize-doc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text.slice(0, 5000), fileName, archetype }),
      signal: controller.signal,
    });
    if (res.ok) {
      const json = await res.json();
      return { summary: json.summary || `Document: ${fileName}`, tags: json.tags || [] };
    }
    if (opts?.throwOnAbortOrTimeout) {
      throw new Error(`summarize HTTP ${res.status}`);
    }
    // Fallback: derive summary from filename
    return { summary: deriveSummaryFromFilename(fileName), tags: deriveTagsFromFilename(fileName), deferred: true };
  } catch (err) {
    if (opts?.throwOnAbortOrTimeout) {
      throw err;
    }
    // Includes AbortError (timeout) and network errors — both degrade to
    // the filename-derived summary so the upload never hangs on summarise.
    return { summary: deriveSummaryFromFilename(fileName), tags: deriveTagsFromFilename(fileName), deferred: true };
  } finally {
    clearTimeout(timer);
    if (callerSignal) {
      callerSignal.removeEventListener('abort', onCallerAbort);
    }
  }
}

/**
 * Save indexed documents to Firestore for persistence.
 */
export async function saveDocumentIndex(documents: DriveDocument[]): Promise<void> {
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return;

    const { doc, setDoc } = await import('firebase/firestore');
    for (const d of documents) {
      await setDoc(doc(db, `users/${user.uid}/driveIndex`, d.id), d);
    }
  } catch (e) {
    console.error('Failed to save drive index:', e);
  }
}

/**
 * Load indexed documents from Firestore.
 */
export async function loadDocumentIndex(): Promise<DriveDocument[]> {
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return [];

    const { collection, getDocs } = await import('firebase/firestore');
    const snap = await getDocs(collection(db, `users/${user.uid}/driveIndex`));
    return snap.docs.map(d => d.data() as DriveDocument);
  } catch {
    return [];
  }
}

/**
 * Format Drive documents as context for Gemini prompts.
 */
export function formatDriveContext(documents: DriveDocument[], tags: string[]): string {
  const tagSet = new Set(tags);
  const relevant = documents.filter(d => d.tags.some(t => tagSet.has(t)));
  if (relevant.length === 0) return '';

  return '\n\nCONTEXT FROM USER DOCUMENTS (Google Drive):\n' +
    relevant.map(d => `- "${d.name}": ${d.summary}`).join('\n');
}

/** Helper: get OAuth client ID from Firebase config or env */
export async function getOAuthClientId(): Promise<string | null> {
  // The OAuth client ID should be configured in the environment (browser Vite env or Node process.env)
  const envClientId =
    (import.meta as { env?: { VITE_GOOGLE_OAUTH_CLIENT_ID?: string } }).env
      ?.VITE_GOOGLE_OAUTH_CLIENT_ID ??
    (typeof process !== 'undefined' ? process.env?.VITE_GOOGLE_OAUTH_CLIENT_ID : undefined);
  return envClientId || null;
}
